import React from 'react';
import { Button, Icon, Menu, Modal, SemanticICONS, Tab } from 'semantic-ui-react';
import { Video } from '@visikon/core-models/content';
import { VideoLibraryContainer } from 'containers/mediaLibrary/VideoLibraryContainer';
import { PLACEHOLDER_VIDEO } from 'commons/utils';
import { BlockListOverview } from 'containers/composer/compositions/CompositionList';
import { VideoFileWrapper } from 'containers/mediaLibrary/VideoFileWrapper';
import { CompositionWrapper } from 'containers/mediaLibrary/CompositionWrapper';
import { ICommonBaseProps, CustomTypeValue } from '../CustomTypeInput';
import { Link } from 'react-router-dom';

export interface IParametersForVideo {
  type: 'video';
  desc: string;
}
export interface IComponentVideoInputProps extends ICommonBaseProps, IParametersForVideo {
  value?: {
    type: 'video' | 'BlockList';
    _idRef: string;
  };

  onChange(key: string, val: CustomTypeValue): void;
}

interface IResourceLinkProps {
  id?: string;
  type?: string;
}

export function VideoTypeInput(props: IComponentVideoInputProps) {
  const [pickerOpen, setPickerOpen] = React.useState(false);

  if (props.isTranslation) {
    return null;
  }

  const dismissPicker = () => {
    setPickerOpen(false);
  };

  const handleVideoPicked = (video: Video) => {
    props.onChange(props.pKey, { _idRef: video._id, type: 'video' });
    setPickerOpen(false);
  };

  const handleCompositionPicked = (id: string) => {
    props.onChange(props.pKey, { _idRef: id, type: 'BlockList' });
    setPickerOpen(false);
  };

  const onVideoClear = () => {
    props.onChange(props.pKey, undefined);
  };

  const hasResource = props.value;
  const buttonText = hasResource ? 'Change resource' : 'Pick resource';

  const tabPanes = [
    {
      menuItem: <Menu.Item key="1_CompositionLibrary">Composition Library</Menu.Item>,
      render: () => (
        <Tab.Pane>
          <BlockListOverview useAsPicker onCompositionPick={(c) => handleCompositionPicked(c)} />
        </Tab.Pane>
      ),
    },
    {
      menuItem: <Menu.Item key="0_VideoLibrary">Video Library</Menu.Item>,
      render: () => (
        <Tab.Pane>
          <VideoLibraryContainer onlyPicker onVideoPick={(v) => handleVideoPicked(v)} />
        </Tab.Pane>
      ),
    },
  ];

  return (
    <>
      <FieldTitle type={props.value?.type} />

      <VideoContentPreview type={props.value?.type} contentId={props.value?._idRef} />
      <br />
      <Button.Group size="large">
        <Button
          onClick={() => {
            setPickerOpen((prevState) => !prevState);
          }}
        >
          {buttonText}
        </Button>

        {hasResource && (
          <>
            <Button onClick={onVideoClear} color="yellow">
              Remove resource
            </Button>
            <JumpToResourceButton id={props.value?._idRef} type={props.value?.type} />
          </>
        )}
      </Button.Group>
      <Modal open={pickerOpen} onClose={() => dismissPicker()} size="fullscreen">
        <Modal.Content style={{ maxHeight: '90vh', overflow: 'auto' }}>
          <Tab panes={tabPanes} />
        </Modal.Content>
      </Modal>
    </>
  );
}

interface VideoTypeConfig<T extends string = string> {
  component: React.ComponentType<any>;
  type: string;
  name: string;
  icon: SemanticICONS;
  idPropName: T;
  url: (id: string) => string;
}

const video_types: VideoTypeConfig[] = [
  { component: VideoTypeInput, type: 'video', name: 'Video', icon: 'video', idPropName: 'videoId', url: (id) => `/media/videos/${id}` },
  {
    component: CompositionWrapper,
    type: 'BlockList',
    name: 'Composition',
    icon: 'file video outline',
    idPropName: 'compositionId',
    url: (id) => `/block/composer/${id}`,
  },
  // For future Platform video integration
  // {
  //   component: CompositionWrapper, // TODO: Change me
  //   type: 'PlatformVideo',
  //   name: 'Platform Video',
  //   icon: 'file video outline',
  //   idPropName: 'todo', // TODO: Change me
  //   url: (id) => `https://qa.app.visikon.com/en/departments/all/videos/${id}`, // TODO: Change me
  // },
];

function getConfigForType(type?: string): VideoTypeConfig | undefined {
  return video_types.find((v) => v.type === type);
}

function VideoContentPreview({ type, contentId }: { type?: string; contentId?: string }) {
  switch (type) {
    case 'BlockList':
      return <CompositionWrapper compositionId={contentId!} />;
    case 'video':
      return <VideoFileWrapper className="ui large bordered image" videoId={contentId!} />;
    default:
      return <img alt="" className="ui large bordered image" src={PLACEHOLDER_VIDEO} />;
  }
}

function FieldTitle({ type }: { type?: string }) {
  const { icon, name } = getConfigForType(type) ?? {};

  return (
    <h4>
      {icon && <Icon name={icon} />}
      {name || 'No resource'}
    </h4>
  );
}

function JumpToResourceButton({ id, type }: IResourceLinkProps) {
  const config = getConfigForType(type);

  if (!config || !id) return null;

  return (
    <Link className="ui button" rel="noreferrer" to={config?.url(id!)}>
      <Icon name={config.icon} />
      Go to resource
    </Link>
  );
}
